import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import type { ComplianceItem, CsvExportRow } from "@/types/domain";
import type { Tables } from "@/integrations/supabase/types";
import { resolveComplianceIconKind } from "@/lib/analytics/complianceStatus";
import { aggregateByStage } from "@/lib/analytics/aggregateByStage";
import { analyzeAssignment } from "@/lib/analytics/analyzeAssignment";
import { type BriefingStageRecord, stagesForDay } from "@/lib/briefingStages";
import { wallClockToUtc, BRIEFING_TZ_DEFAULT } from "@/lib/briefingTime";
import {
  effectiveShiftStartParts,
  effectiveShiftEndParts,
  festivalDayBoundsParts,
  shiftDurationMinutes,
} from "@produce/shared";
import { summarizeMatchConfidence } from "@/lib/matchConfidence";
import { MatchConfidenceBadge } from "@/components/MatchConfidenceBadge";
import { matchAssignmentToPresence } from "@/lib/assignmentStageMatch";
import ProductionSidebar from "@/components/ProductionSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { defaultEventDate, festivalDayIso, eventDatesBetween } from "@produce/shared";
import {
  assignmentsForDayQuery,
  briefingDayDoorsQuery,
  briefingStagesQuery,
  crewMembersForDayQuery,
  producerBriefingsQuery,
} from "@/lib/queries/productionQueries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import { supabase } from "@/integrations/supabase/client";
import { fetchCrewPushUserIds, isSharingWithProducer } from "@produce/shared";
import { useAuth } from "@/contexts/AuthContext";
import { planAllowsAnalytics } from "@/lib/productionTierAccess";
import { useProductionTierAccess } from "@/hooks/useProductionTierAccess";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { format } from "date-fns";
import { doorsForDay } from "@/lib/doorsForDay";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import {
  MapPin,
  Users,
  CheckCircle,
  XCircle,
  WifiOff,
  FileText,
  Printer,
  RefreshCw,
  Search,
  Bell,
  BellOff,
  Radio,
  Lock,
  Pencil,
  HelpCircle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { getStageColor } from "@/lib/stageColors";

type CrewMemberWithStatus = Tables<"crew_members"> & {
  hasGPS: boolean;
  gpsLive: boolean;
  lastLocation: string | null;
  pushEnabled: boolean;
};

interface StagePresenceLog {
  id: string;
  briefing_id: string;
  user_id: string;
  stage_zone_id: string;
  entered_at: string;
  left_at: string | null;
  // Source columns added in 20260502140000_zone_match_within_accuracy_fallback.
  // NULL on rows that predate the migration; the backfill function
  // (20260504100000_backfill_zone_match_source) fills as many as it
  // can but rows whose neighbouring crew_locations has been pruned
  // by retention stay NULL.
  entry_match_source: "strict" | "within_accuracy" | null;
  entry_match_distance_m: number | null;
  festival_area: {
    name: string;
    stage_id: string;
    briefing_stage_id: string | null;
  };
}

interface CrewMember {
  id: string;
  name: string;
  function: string;
  user_id: string;
  color: string | null;
}

interface Assignment {
  id: string;
  crew_member_id: string;
  stage_id: string | null;
  stage_name: string;
  artist_name: string | null;
  subject: string | null;
  filming_start: string;
  filming_end: string;
  event_date: string;
}

interface AssignmentOverride {
  id: string;
  assignment_id: string;
  briefing_id: string;
  was_present: boolean;
  present_minutes: number | null;
  note: string | null;
  created_by: string;
}

// Format time ago in a human-readable way
const formatTimeAgo = (timestampMs: number): string => {
  const diffMs = Date.now() - timestampMs;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const hours = Math.floor(diffMinutes / 60);
  const mins = diffMinutes % 60;

  if (hours < 24) return `${hours}h ${mins}m ago`;

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h ago`;
};

// Calculate downtime (GPS gaps) for a crew member across the whole day
const calculateDowntime = (locations: Tables<"crew_locations">[], userId: string): number => {
  const userLocations = locations
    .filter((loc) => loc.user_id === userId)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (userLocations.length < 2) return 0;

  let downtimeMinutes = 0;
  const GAP_THRESHOLD = 5;

  for (let i = 1; i < userLocations.length; i++) {
    const diff =
      (new Date(userLocations[i].timestamp).getTime() -
        new Date(userLocations[i - 1].timestamp).getTime()) /
      60000;
    if (diff > GAP_THRESHOLD) downtimeMinutes += diff;
  }

  return Math.round(downtimeMinutes);
};

const Analytics = () => {
  // Honours the producer's 12h/24h setting — this rendered a raw HH:MM cut.
  const formatTime = useProducerTimeFormatter();
  const navigate = useNavigate();
  const { productionId: briefingId } = useParams();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [complianceSortBy, setComplianceSortBy] = useState<"time" | "crew" | "stage">("time");
  const [complianceStageFilter, setComplianceStageFilter] = useState<string>("all");
  const [complianceCrewFilter, setComplianceCrewFilter] = useState<string>("all");
  const [editingAssignment, setEditingAssignment] = useState<string | null>(null);
  const [overridePresent, setOverridePresent] = useState(true);
  const [overrideMinutes, setOverrideMinutes] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [editingAssignmentDuration, setEditingAssignmentDuration] = useState(0);
  const { expanded } = useSidebar();

  // Analytics access (STUDIO). A co-producer inherits the owner's plan.
  const { hasAccess: hasAnalyticsAccess, isLoading: isAccessLoading } = useProductionTierAccess(
    briefingId,
    planAllowsAnalytics,
  );

  // Fetch briefings
  const { data: briefings } = useQuery({
    ...producerBriefingsQuery(session?.user?.id),
    enabled: !!session?.user?.id,
  });

  const currentBriefing = briefings?.find((b) => b.id === briefingId);

  // Per-day doors overrides — without these the GPS uptime calc
  // for day N used the briefing-level fallback (often "08:00 - 23:00"),
  // which over- or under-estimated coverage when the producer set a
  // different window for that specific day (e.g. day 4 running 14:00 - 03:00).
  const { data: dayDoors = [] } = useQuery({
    ...briefingDayDoorsQuery(briefingId),
    enabled: !!briefingId,
  });

  // Doors window for the current selectedDay, resolving per-day
  // override → briefing-level fallback. Used by both the GPS query
  // end-date IIFE and the GPS uptime IIFE below.
  const selectedDayDoors =
    currentBriefing && selectedDay
      ? doorsForDay({
          perDay: dayDoors,
          fallback: {
            doors_open: currentBriefing.doors_open ?? null,
            doors_close: currentBriefing.doors_close ?? null,
          },
          eventDate: selectedDay,
        })
      : { doors_open: null, doors_close: null };

  const briefingTz = currentBriefing?.timezone || BRIEFING_TZ_DEFAULT;

  // Shared day expansion, same as the schedule and live-tracking pages. This
  // page reads crew and assignments through the shared day-scoped queries, so
  // a differently-derived day here would filter on an event_date no row has.
  const eventDays = eventDatesBetween(
    currentBriefing?.festival_date_start,
    currentBriefing?.festival_date_end,
  );

  useEffect(() => {
    if (eventDays.length > 0 && !selectedDay) {
      // Festival day, not calendar day: a 00:30 shift belongs to yesterday's
      // event_date, and the producer may be watching from another timezone.
      setSelectedDay(defaultEventDate(eventDays, festivalDayIso(new Date(), briefingTz)));
    }
  }, [eventDays, selectedDay, briefingTz]);

  useEffect(() => {
    if (briefings && briefings.length > 0 && !briefingId) {
      navigate(`/analytics?briefing=${briefings[0].id}`, { replace: true });
    }
  }, [briefings, briefingId, navigate]);

  // Fetch stage presence logs.
  // Day window is built from the briefing's TZ, not the browser's:
  // a producer reviewing the festival from another zone still gets
  // the same logs the producer at the festival site would see.
  const {
    data: presenceLogs,
    refetch: refetchPresence,
    isFetched: presenceFetched,
  } = useQuery({
    queryKey: ["stage-presence", briefingId, selectedDay, briefingTz],
    queryFn: async () => {
      if (!briefingId || !selectedDay) return [];
      // Festival "day" runs 06:00 → 06:00 next-day in briefing TZ so that
      // late-night shifts labeled with event_date=selectedDay (e.g. a 00:15
      // shift conceptually part of "Day 1") are included in this window.
      const bounds = festivalDayBoundsParts(selectedDay);
      const dayStartUtc = wallClockToUtc(bounds.start.date, bounds.start.time, briefingTz);
      const dayEndUtc = wallClockToUtc(bounds.end.date, bounds.end.time, briefingTz);

      const { data, error } = await supabase
        .from("stage_presence_logs")
        .select(
          `
          *,
          festival_area:festival_areas(name, stage_id, briefing_stage_id)
        `,
        )
        .eq("briefing_id", briefingId)
        .gte("entered_at", dayStartUtc.toISOString())
        .lt("entered_at", dayEndUtc.toISOString())
        .order("entered_at", { ascending: true });

      if (error) throw error;
      return data as StagePresenceLog[];
    },
    enabled: !!briefingId && !!selectedDay,
  });

  // Fetch crew members
  const { data: crewMembers } = useQuery({
    ...crewMembersForDayQuery(briefingId, selectedDay),
    enabled: !!briefingId && !!selectedDay,
    select: (rows) => rows as CrewMember[],
  });

  // Fetch assignments
  const { data: assignments } = useQuery({
    ...assignmentsForDayQuery(briefingId, selectedDay),
    enabled: !!briefingId && !!selectedDay,
    select: (rows) => rows as unknown as Assignment[],
  });

  // Fetch festival areas. Used downstream to map briefing_stage_id ↔
  // polygon UUIDs so per-assignment categorization can answer "was the
  // user at the right polygon during their shift?".
  const { data: festivalAreas } = useQuery({
    queryKey: ["festival-areas", briefingId],
    queryFn: async () => {
      if (!briefingId) return [];

      const { data, error } = await supabase
        .from("festival_areas")
        .select("*")
        .eq("briefing_id", briefingId);

      if (error) throw error;
      return data;
    },
    enabled: !!briefingId,
  });

  // Fetch assignment overrides
  const { data: assignmentOverrides } = useQuery({
    queryKey: ["assignment-overrides", briefingId],
    queryFn: async () => {
      if (!briefingId) return [];

      const { data, error } = await supabase
        .from("assignment_overrides")
        .select("*")
        .eq("briefing_id", briefingId);

      if (error) throw error;
      return data as AssignmentOverride[];
    },
    enabled: !!briefingId,
  });

  // Fetch crew completed assignments (manual "mark as done")
  const { data: crewCompletions } = useQuery({
    queryKey: ["crew-completions", briefingId],
    queryFn: async () => {
      if (!briefingId) return [];

      // Producer can see completions for their briefings via RLS
      const { data, error } = await supabase
        .from("crew_completed_assignments")
        .select("assignment_id, user_id, completed_at");

      if (error) {
        return [];
      }
      return data || [];
    },
    enabled: !!briefingId,
  });

  const crewCompletionSet = new Set((crewCompletions || []).map((c) => c.assignment_id));

  // Save override mutation
  const saveOverrideMutation = useMutation({
    mutationFn: async (params: {
      assignmentId: string;
      wasPresent: boolean;
      presentMinutes: number | null;
      note: string;
    }) => {
      if (!briefingId || !session?.user?.id) throw new Error("Missing context");

      // Check if override already exists
      const existing = assignmentOverrides?.find((o) => o.assignment_id === params.assignmentId);

      if (existing) {
        const { error } = await supabase
          .from("assignment_overrides")
          .update({
            was_present: params.wasPresent,
            present_minutes: params.presentMinutes,
            note: params.note || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("assignment_overrides").insert({
          assignment_id: params.assignmentId,
          briefing_id: briefingId,
          was_present: params.wasPresent,
          present_minutes: params.presentMinutes,
          note: params.note || null,
          created_by: session.user.id,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assignment-overrides", briefingId] });
      setEditingAssignment(null);
      setOverrideMinutes("");
      setOverrideNote("");
    },
  });

  // Canonical stage list for this briefing, sourced from the new
  // briefing_stages table. Used for the per-stage analytics rollup
  // below so non-artist assignments (e.g. "Publiek op Mainstage")
  // count under their stage even when the crew member has no GPS
  // presence-log entries.
  const { data: briefingStageRecords = [] } = useQuery({
    ...briefingStagesQuery(briefingId),
    enabled: !!briefingId,
    select: (rows) => rows as BriefingStageRecord[],
  });

  // Helper to resolve a presence-log's festival_area to its display
  // label. FK-first: festival_areas.briefing_stage_id ↔ briefing_stages.id
  // (post-migration 20260504240000). Falls back to JSONB-bridge
  // (festival_area.stage_id legacy slug → briefings.stages[].label)
  // and finally to festival_areas.name for rows that pre-date both.
  const briefingStagesJsonForLabel =
    (currentBriefing as { stages?: { name: string; label: string }[] } | undefined)?.stages ?? [];
  const getStageLabel = (
    area:
      | { name?: string | null; stage_id?: string | null; briefing_stage_id?: string | null }
      | null
      | undefined,
  ): string => {
    if (!area) return "Unknown";
    if (area.briefing_stage_id) {
      const matched = briefingStageRecords.find((s) => s.id === area.briefing_stage_id);
      if (matched) return matched.label;
    }
    if (area.stage_id) {
      const jsonbHit = briefingStagesJsonForLabel.find((s) => s?.name === area.stage_id);
      if (jsonbHit?.label) return jsonbHit.label;
      const labelHit = briefingStageRecords.find((s) => s.label === area.stage_id);
      if (labelHit) return labelHit.label;
    }
    return area.name ?? area.stage_id ?? "Unknown";
  };

  // Fetch crew locations for GPS reliability. For overnight events
  // (doorsClose < doorsOpen for THIS day) we also fetch next day's
  // early hours. When doors aren't configured we don't extend the
  // range — there's no meaningful overnight signal to act on.
  const gpsQueryEndDate = (() => {
    if (!selectedDay) return selectedDay;
    const doorsOpen = selectedDayDoors.doors_open;
    const doorsClose = selectedDayDoors.doors_close;
    if (doorsOpen && doorsClose && doorsClose < doorsOpen) {
      const next = new Date(selectedDay);
      next.setDate(next.getDate() + 1);
      return next.toISOString().split("T")[0];
    }
    return selectedDay;
  })();

  const { data: gpsLocations } = useQuery({
    queryKey: ["gps-locations", briefingId, selectedDay, gpsQueryEndDate],
    queryFn: async () => {
      if (!briefingId || !selectedDay) return [];

      const { data, error } = await supabase
        .from("crew_locations")
        .select("*")
        .eq("briefing_id", briefingId)
        // Producer-facing analytics respect the per-crew opt-out.
        .eq("visible_to_producer", true)
        .gte("timestamp", `${selectedDay}T00:00:00`)
        .lte("timestamp", `${gpsQueryEndDate}T23:59:59`)
        .order("timestamp", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!briefingId && !!selectedDay,
  });

  // Fetch crew status for live monitoring
  const { data: crewStatus, refetch: refetchCrewStatus } = useQuery({
    queryKey: ["crew-status", briefingId, selectedDay],
    queryFn: async () => {
      if (!briefingId || !selectedDay) return [];

      const { data: crewData, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay);

      if (error) throw error;

      // One call for the whole production. Producers can no longer read
      // their crew's push_subscriptions rows (those hold the Expo tokens);
      // the RPC answers "can the crew app reach them" and nothing more.
      const crewPushUserIds = await fetchCrewPushUserIds(supabase, briefingId);

      const crewWithStatus = await Promise.all(
        (crewData || []).map(async (crew) => {
          if (!crew.user_id)
            return {
              ...crew,
              hasGPS: false,
              gpsLive: false,
              lastLocation: null,
              pushEnabled: false,
            };

          const { data: crewSettings } = await supabase
            .from("crew_settings")
            .select("location_tracking_enabled, share_location_with_producer")
            .eq("user_id", crew.user_id)
            .maybeSingle();

          const { data: location } = await supabase
            .from("crew_locations")
            .select("timestamp, accuracy")
            .eq("user_id", crew.user_id)
            .eq("briefing_id", briefingId)
            // "GPS live" badge is producer-facing too — don't leak
            // activity status for crew who opted out of sharing.
            .eq("visible_to_producer", true)
            .order("timestamp", { ascending: false })
            .limit(1)
            .maybeSingle();

          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
          const gpsLive = location && location.timestamp > fiveMinutesAgo;

          return {
            ...crew,
            // Both switches, not just the master one. Reading only
            // location_tracking_enabled reported "has GPS" for a crew member
            // who had turned off sharing with the producer — while the live
            // tracking page, one click away, correctly said "Tracking off".
            // No settings row is NOT "disabled" — the columns default to on
            // and get_crew_tracking_consent COALESCEs a missing row to true,
            // so guarding on `!!crewSettings` here would put this page and
            // live tracking back in disagreement, just the other way round.
            hasGPS: isSharingWithProducer(crewSettings),
            gpsLive: gpsLive || false,
            lastLocation: location?.timestamp || null,
            pushEnabled: crewPushUserIds.has(crew.user_id),
          };
        }),
      );

      return crewWithStatus;
    },
    enabled: !!briefingId && !!selectedDay,
  });

  // Real-time updates for crew status
  useEffect(() => {
    if (!briefingId) return;

    const channel = supabase
      .channel("crew-status-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "crew_locations",
          filter: `briefing_id=eq.${briefingId}`,
        },
        () => {
          refetchCrewStatus();
        },
      )
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "crew_profiles" }, () => {
        refetchCrewStatus();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [briefingId, refetchCrewStatus]);

  // Calculate duration in minutes
  const getDuration = (entered: string, left: string | null): number => {
    const enteredTime = new Date(entered).getTime();
    const leftTime = left ? new Date(left).getTime() : new Date().getTime();
    return Math.round((leftTime - enteredTime) / 60000);
  };

  // Format duration
  const formatDuration = (minutes: number): string => {
    const rounded = Math.round(minutes);
    const hours = Math.floor(rounded / 60);
    const mins = rounded % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Everything below summarises the stage-presence logs. Until those have
  // landed the sums are all zero — rendering them would flash "0m (0%)" and
  // then correct itself, which reads as broken rather than as loading. The
  // crew list and assignments arrive earlier, so this gate is the difference
  // between a wrong number and a loading state.
  const presenceReady = !selectedDay || presenceFetched;
  const emptyStateLabel = presenceReady ? "No data available" : "Loading\u2026";

  // Calculate crew performance stats
  const crewPerformance = !presenceReady
    ? []
    : crewMembers?.map((crew) => {
        const memberLogs = presenceLogs?.filter((log) => log.user_id === crew.user_id) || [];
        const totalMinutes = memberLogs.reduce(
          (sum, log) => sum + getDuration(log.entered_at, log.left_at),
          0,
        );
        const avgMinutes = memberLogs.length > 0 ? Math.round(totalMinutes / memberLogs.length) : 0;
        const stagesVisited = new Set(memberLogs.map((log) => getStageLabel(log.festival_area)));

        const stageCounts = memberLogs.reduce(
          (acc, log) => {
            const stageName = getStageLabel(log.festival_area);
            acc[stageName] = (acc[stageName] || 0) + getDuration(log.entered_at, log.left_at);
            return acc;
          },
          {} as Record<string, number>,
        );

        const mostVisitedStage = Object.entries(stageCounts).sort((a, b) => b[1] - a[1])[0];
        const downtime = gpsLocations ? calculateDowntime(gpsLocations, crew.user_id) : 0;

        return {
          crew,
          totalStages: stagesVisited.size,
          totalMinutes,
          avgMinutes,
          visits: memberLogs.length,
          downtime,
          mostVisitedStage: mostVisitedStage
            ? { name: mostVisitedStage[0], minutes: mostVisitedStage[1] }
            : null,
        };
      }) || [];

  // Calculate stage coverage stats. Combines presence-based metrics
  // with assignment counts so a crew assignment like "Publiek op
  // Mainstage" rolls up under Mainstage even when GPS never logged
  // the crew member entering that zone.
  const stageCoverage = (() => {
    if (!presenceReady) return [];
    // briefing_stages is per-day post-20260506001300 (one row per
    // (briefing, event_date, display_order)). stagesForDay filters
    // by event_date so each day's chart only shows that day's stages.
    const stages = (selectedDay ? stagesForDay(briefingStageRecords, selectedDay) : []).map(
      (s) => ({
        id: s.id,
        label: s.label,
        display_order: s.display_order,
      }),
    );
    // Presence input mapping. FK fast-path:
    // festival_areas.briefing_stage_id ↔ briefing_stages.id
    // (post-migration 20260504240000). Falls through to JSONB
    // legacy-slug bridge then festival_areas.name for rows whose FK
    // hasn't backfilled.
    const briefingStagesJson =
      (currentBriefing as { stages?: { name: string; label: string }[] } | undefined)?.stages ?? [];
    const labelByLegacyStageId = new Map<string, string>();
    for (const s of briefingStagesJson) {
      if (s?.name && s?.label) labelByLegacyStageId.set(s.name, s.label);
    }
    const presenceInputs = (presenceLogs ?? []).map((log) => {
      const area = log.festival_area;
      const fkStageId = area?.briefing_stage_id ?? null;
      const legacy = area?.stage_id ?? null;
      const fallbackLabel =
        (legacy && labelByLegacyStageId.get(legacy)) || area?.name || legacy || null;
      return {
        stage_id: fkStageId,
        stage_name: fallbackLabel,
        durationMinutes: getDuration(log.entered_at, log.left_at),
      };
    });
    // Per-assignment detection via analyzeAssignment(): instead of a
    // boolean "did presence_log overlap shift?", we get a 4-way
    // category (detected / not_tracked / elsewhere / data_inconsistent)
    // plus clipped covered-minutes. The category lets the coverage
    // metric exclude "not_tracked" assignments (= bundle-bug, app off)
    // from the denominator, so producer scores aren't dragged by
    // infrastructure failures.
    const crewIdToUserId = new Map<string, string | null>();
    for (const cm of crewMembers ?? []) {
      crewIdToUserId.set(cm.id, cm.user_id ?? null);
    }

    // Map briefing_stage.id → set of festival_area.id values that resolve
    // to it. A stage can have multiple polygons (one per day_index).
    const polygonIdsByStageId = new Map<string, Set<string>>();
    for (const fa of festivalAreas ?? []) {
      const sid = (fa as { briefing_stage_id?: string | null }).briefing_stage_id;
      if (!sid) continue;
      let set = polygonIdsByStageId.get(sid);
      if (!set) {
        set = new Set<string>();
        polygonIdsByStageId.set(sid, set);
      }
      set.add((fa as { id: string }).id);
    }

    // Shape pings + presence_logs for analyzeAssignment(). Filter pings
    // to producer-visible ones (gpsLocations is already producer-scoped).
    const pingsInput = (gpsLocations ?? []).map((cl) => ({
      user_id: cl.user_id,
      timestamp: cl.timestamp,
      current_stage_zone_id: cl.current_stage_zone_id ?? null,
    }));
    const presenceLogsInput = (presenceLogs ?? []).map((log) => ({
      user_id: log.user_id,
      entered_at: log.entered_at,
      left_at: log.left_at,
      briefing_stage_id: log.festival_area?.briefing_stage_id ?? null,
      stage_zone_id: log.stage_zone_id,
    }));

    // Skip "Other"/custom assignments without a stage_id. They can never be
    // GPS-detected per-stage (no polygon to match), so including them in
    // the denominator would falsely deflate scores.
    const assignmentInputs = (assignments ?? [])
      .filter((a) => a.stage_id != null)
      .map((a) => {
        const durationMinutes = shiftDurationMinutes(a.filming_start, a.filming_end);
        const userId = crewIdToUserId.get(a.crew_member_id ?? "") ?? null;
        const sp = effectiveShiftStartParts(a.event_date, a.filming_start);
        const ep = effectiveShiftEndParts(a.event_date, a.filming_start, a.filming_end);
        const startUtcMs = wallClockToUtc(sp.date, sp.time, briefingTz).getTime();
        const endUtcMs = wallClockToUtc(ep.date, ep.time, briefingTz).getTime();

        const analysis = analyzeAssignment(
          {
            user_id: userId,
            stage_id: a.stage_id ?? null,
            startUtcMs,
            endUtcMs,
            expectedMinutes: durationMinutes,
          },
          {
            pings: pingsInput,
            presenceLogs: presenceLogsInput,
            polygonIdsByStageId,
          },
        );

        return {
          stage_id: a.stage_id ?? null,
          stage_name: a.stage_name ?? null,
          durationMinutes,
          detected: analysis.category === "detected" || analysis.category === "data_inconsistent",
          category: analysis.category,
          coveredMinutes: analysis.coveredMinutes,
        };
      });

    return aggregateByStage({
      stages,
      assignments: assignmentInputs,
      presenceLogs: presenceInputs,
    }).map((r) => {
      // Coverage % = covered shift-minutes / trackable expected shift-minutes.
      // Bounded ≤100% by construction (analyzeAssignment caps each
      // assignment's covered at its own expected). "Not tracked"
      // assignments (bundle bug, app off) are excluded from BOTH
      // numerator and denominator — they're unknowns, not misses.
      const coveragePct =
        r.trackableExpectedMinutes > 0
          ? Math.round((r.coveredAssignmentMinutes / r.trackableExpectedMinutes) * 100)
          : null;
      const status: "covered" | "partial" | "low" | "no_gps" | "n_a" =
        r.assignmentCount === 0
          ? "n_a"
          : r.trackableExpectedMinutes === 0
            ? "no_gps"
            : coveragePct != null && coveragePct >= 50
              ? "covered"
              : coveragePct != null && coveragePct >= 10
                ? "partial"
                : "low";
      return {
        stage: r.label,
        stageId: r.stageId,
        totalMinutes: r.presenceTotalMinutes,
        expectedMinutes: r.expectedTotalMinutes,
        // Covered = sum of per-assignment clipped overlap (bounded ≤ expected).
        // Trackable expected = total expected minus not_tracked assignments.
        coveredMinutes: r.coveredAssignmentMinutes,
        trackableExpectedMinutes: r.trackableExpectedMinutes,
        coveragePct,
        visits: r.presenceVisits,
        avgMinutes:
          r.presenceVisits > 0 ? Math.round(r.presenceTotalMinutes / r.presenceVisits) : 0,
        assignmentCount: r.assignmentCount,
        detectedAssignmentCount: r.detectedAssignmentCount,
        // 4-way diagnosis counts. UI can show "X detected / Y not tracked /
        // Z elsewhere" so a 0% coverage doesn't mask why.
        detectedCount: r.detectedCount,
        notTrackedCount: r.notTrackedCount,
        elsewhereCount: r.elsewhereCount,
        dataInconsistentCount: r.dataInconsistentCount,
        status,
      };
    });
  })();

  // Calculate assignment compliance with GPS gap info.
  // Same presence gate as the two collections above: without it this table
  // renders every named crew member as "not present, 0%" until the logs land,
  // which reads as a verdict about people rather than as loading.
  const assignmentCompliance = !presenceReady
    ? []
    : (assignments
        ?.map((assignment) => {
          const crew = crewMembers?.find((c) => c.id === assignment.crew_member_id);
          if (!crew) return null;

          // Midnight-aware shift window: late-night shifts (filming_start < 06:00
          // labeled with event_date=X) actually happen on calendar day X+1, and
          // shifts that cross midnight within themselves get their end pushed +1.
          const startParts = effectiveShiftStartParts(
            assignment.event_date,
            assignment.filming_start,
          );
          const endParts = effectiveShiftEndParts(
            assignment.event_date,
            assignment.filming_start,
            assignment.filming_end,
          );
          const assignmentStart = wallClockToUtc(startParts.date, startParts.time, briefingTz);
          const assignmentEnd = wallClockToUtc(endParts.date, endParts.time, briefingTz);
          const assignmentDuration = Math.max(
            1,
            (assignmentEnd.getTime() - assignmentStart.getTime()) / 60000,
          );

          const relevantLogs =
            presenceLogs?.filter((log) => {
              if (log.user_id !== crew.user_id) return false;
              // FK-first match. Both crew_assignments.stage_id and
              // festival_areas.briefing_stage_id are UUIDs pointing at
              // briefing_stages.id post-migration 20260504240000;
              // matchAssignmentToPresence short-circuits on UUID compare
              // when both are present. Falls through to the legacy
              // JSONB bridge for partially-migrated rows.
              if (
                !matchAssignmentToPresence({
                  assignmentStageName: assignment.stage_name,
                  assignmentStageId: assignment.stage_id ?? null,
                  presenceArea: log.festival_area,
                  briefing: currentBriefing,
                })
              ) {
                return false;
              }
              const entered = new Date(log.entered_at);
              const left = log.left_at ? new Date(log.left_at) : new Date();
              return entered < assignmentEnd && left > assignmentStart;
            }) || [];

          const presentMinutes = relevantLogs.reduce((sum, log) => {
            const entered = new Date(log.entered_at);
            const left = log.left_at ? new Date(log.left_at) : new Date();
            const overlapStart = Math.max(entered.getTime(), assignmentStart.getTime());
            const overlapEnd = Math.min(left.getTime(), assignmentEnd.getTime());
            return sum + Math.max(0, (overlapEnd - overlapStart) / 60000);
          }, 0);

          const attendance = Math.min(100, Math.round((presentMinutes / assignmentDuration) * 100));

          // Confidence summary across the relevant presence logs.
          // Conservative: any NULL or any drift fallback wins over
          // strict, so the producer doesn't trust an inflated %.
          const matchConfidence = summarizeMatchConfidence(relevantLogs);

          // Check for override
          const override = assignmentOverrides?.find((o) => o.assignment_id === assignment.id);

          // Check if crew member manually marked this as done
          const markedAsDone = crewCompletionSet.has(assignment.id);

          // Determine final attendance: override > manual completion > GPS data
          let finalAttendance = attendance;
          // Present := attendance >= 50%, OR drift (within_accuracy) with
          // any presence at all. Drift means "GPS placed crew Xm
          // outside the polygon edge"; the badge documents the
          // uncertainty, but the producer-facing question "were they
          // there?" is yes when there's any matching presence.
          const driftPresent = matchConfidence.source === "within_accuracy" && presentMinutes > 0;
          let finalPresent = attendance >= 50 || driftPresent;
          let finalPresentMinutes = presentMinutes;

          if (override) {
            finalAttendance = override.was_present ? 100 : 0;
            finalPresent = override.was_present;
            finalPresentMinutes = override.was_present
              ? (override.present_minutes ?? presentMinutes)
              : 0;
          } else if (markedAsDone) {
            finalPresent = true;
            // Keep GPS-based attendance but ensure it shows as present
            finalAttendance = Math.max(attendance, 100);
            finalPresentMinutes = Math.max(presentMinutes, assignmentDuration);
          }

          return {
            assignment,
            crew,
            attendance: finalAttendance,
            present: finalPresent,
            presentMinutes: finalPresentMinutes,
            totalMinutes: assignmentDuration,
            override,
            markedAsDone,
            rawAttendance: attendance,
            rawPresentMinutes: presentMinutes,
            matchConfidence,
          };
        })
        .filter(Boolean) as ComplianceItem[]) || [];

  // Get unique stages and crew members for filter dropdowns
  const uniqueStagesForFilter = [
    ...new Set(assignmentCompliance.map((c: ComplianceItem) => c.assignment.stage_name)),
  ].sort();
  const uniqueCrewForFilter = [
    ...new Map(assignmentCompliance.map((c: ComplianceItem) => [c.crew.id, c.crew])).values(),
  ].sort((a: Tables<"crew_members">, b: Tables<"crew_members">) => a.name.localeCompare(b.name));

  // Filter and sort assignment compliance
  const filteredAssignmentCompliance = assignmentCompliance
    .filter((comp: ComplianceItem) => {
      if (assignmentSearch) {
        const searchLower = assignmentSearch.toLowerCase();
        const matchesSearch =
          comp.crew.name.toLowerCase().includes(searchLower) ||
          (comp.assignment.artist_name || comp.assignment.subject || "")
            .toLowerCase()
            .includes(searchLower) ||
          comp.assignment.stage_name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      if (complianceStageFilter !== "all" && comp.assignment.stage_name !== complianceStageFilter)
        return false;
      if (complianceCrewFilter !== "all" && comp.crew.id !== complianceCrewFilter) return false;
      return true;
    })
    .sort((a: ComplianceItem, b: ComplianceItem) => {
      switch (complianceSortBy) {
        case "time":
          return a.assignment.filming_start.localeCompare(b.assignment.filming_start);
        case "crew":
          return a.crew.name.localeCompare(b.crew.name);
        case "stage":
          return a.assignment.stage_name.localeCompare(b.assignment.stage_name);
        default:
          return 0;
      }
    });

  // Prepare horizontal bar chart data for stage coverage
  // Export to CSV function
  const exportToCSV = (data: CsvExportRow[], filename: string, headers: string[]) => {
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header.toLowerCase().replace(/\s+/g, "_")];
            return typeof value === "string" && value.includes(",") ? `"${value}"` : value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}_${selectedDay}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCrewPerformanceCSV = () => {
    // presenceReady, not just "have we got a day": these rows are empty while
    // the presence logs are in flight, and an export of nothing but headers
    // reads as "nobody was present that day".
    if (!selectedDay || !currentBriefing || !presenceReady) return;
    const data = crewPerformance.map((perf) => ({
      crew_member: perf.crew.name,
      function: perf.crew.function,
      stages_visited: perf.totalStages,
      downtime: formatDuration(perf.downtime),
      visits: perf.visits,
      avg_per_visit: formatDuration(perf.avgMinutes),
      most_time_at: perf.mostVisitedStage?.name || "N/A",
      most_time_duration: perf.mostVisitedStage
        ? formatDuration(perf.mostVisitedStage.minutes)
        : "N/A",
    }));
    exportToCSV(data, `crew_performance_${currentBriefing?.festival_name || "analytics"}`, [
      "Crew member",
      "Function",
      "Stages visited",
      "Downtime",
      "Visits",
      "Avg per visit",
      "Most time at",
      "Most time duration",
    ]);
  };

  const exportStageCoverageCSV = () => {
    if (!selectedDay || !currentBriefing || !presenceReady) return;
    const data = stageCoverage.map((stage) => ({
      stage: stage.stage,
      detected: formatDuration(stage.totalMinutes),
      expected: stage.expectedMinutes > 0 ? formatDuration(stage.expectedMinutes) : "—",
      coverage_pct: stage.coveragePct != null ? `${stage.coveragePct}%` : "—",
      status: stage.status,
      assignments_detected:
        stage.assignmentCount > 0
          ? `${stage.detectedAssignmentCount}/${stage.assignmentCount}`
          : "—",
      visits: stage.visits,
    }));
    exportToCSV(data, `stage_coverage_${currentBriefing?.festival_name || "analytics"}`, [
      "Stage",
      "Detected",
      "Expected",
      "Coverage pct",
      "Status",
      "Assignments detected",
      "Visits",
    ]);
  };

  const exportComplianceCSV = () => {
    if (!selectedDay || !currentBriefing || assignmentCompliance.length === 0) return;
    const data = assignmentCompliance.map((comp: ComplianceItem) => ({
      assignment: comp.assignment.artist_name || comp.assignment.subject,
      crew_member: comp.crew.name,
      function: comp.crew.function,
      stage: comp.assignment.stage_name,
      time_start: comp.assignment.filming_start.substring(0, 5),
      time_end: comp.assignment.filming_end.substring(0, 5),
      present: comp.present ? "Yes" : "No",
      attendance: `${comp.attendance}%`,
      present_time: formatDuration(Math.round(comp.presentMinutes)),
      total_time: formatDuration(Math.round(comp.totalMinutes)),
    }));
    exportToCSV(data, `assignment_compliance_${currentBriefing?.festival_name || "analytics"}`, [
      "Assignment",
      "Crew member",
      "Function",
      "Stage",
      "Time start",
      "Time end",
      "Present",
      "Attendance",
      "Present time",
      "Total time",
    ]);
  };

  const handleRefresh = () => {
    refetchPresence();
    refetchCrewStatus();
  };

  const exportToPDF = async () => {
    if (!currentBriefing || !selectedDay) return;
    try {
      const response = await supabase.functions.invoke("export-analytics-pdf", {
        body: { briefingId: currentBriefing.id, selectedDay },
      });
      if (response.error) throw response.error;
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Analytics_${currentBriefing.festival_name || "Production"}_${selectedDay}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenOverrideDialog = (comp: ComplianceItem) => {
    setEditingAssignment(comp.assignment.id);
    setEditingAssignmentDuration(Math.round(comp.totalMinutes));
    if (comp.override) {
      setOverridePresent(comp.override.was_present);
      setOverrideMinutes(comp.override.present_minutes?.toString() || "");
      setOverrideNote(comp.override.note || "");
    } else {
      setOverridePresent(comp.present);
      setOverrideMinutes(Math.round(comp.presentMinutes).toString());
      setOverrideNote("");
    }
  };

  const handleSaveOverride = () => {
    if (!editingAssignment) return;
    saveOverrideMutation.mutate({
      assignmentId: editingAssignment,
      wasPresent: overridePresent,
      presentMinutes: overridePresent ? (overrideMinutes ? parseFloat(overrideMinutes) : null) : 0,
      note: overrideNote,
    });
  };

  useEffect(() => {
    if (!briefingId) navigate("/dashboard");
  }, [briefingId, navigate]);

  if (!briefingId) return null;

  return (
    <div className="flex h-screen bg-background">
      <ProductionSidebar productionId={briefingId} />

      {!hasAnalyticsAccess && !isAccessLoading ? (
        <div
          className="flex flex-1 flex-col items-center justify-center px-6 text-center transition-all duration-300"
          style={{ marginLeft: expanded ? "220px" : "72px" }}
        >
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h1 className="mb-3 text-2xl font-bold">Analytics</h1>
          <p className="mb-6 max-w-md text-muted-foreground">
            Analytics is only available on the STUDIO plan. Get insights into crew performance,
            stage coverage, and GPS tracking reliability.
          </p>
          <Button onClick={() => navigate("/upgrade")}>Upgrade to STUDIO</Button>
        </div>
      ) : (
        <PageTransition
          className="flex flex-1 flex-col overflow-hidden transition-all duration-300"
          style={{ marginLeft: expanded ? "220px" : "72px" }}
        >
          {/* Header */}
          <div className="border-b border-border bg-background px-6 py-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold">Analytics</h1>
                <p className="text-sm text-muted-foreground">
                  Stage presence and crew performance insights
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {eventDays.length > 1 && (
                <Tabs value={selectedDay} onValueChange={setSelectedDay}>
                  <TabsList>
                    {eventDays.map((day, index) => (
                      <TabsTrigger key={day} value={day}>
                        Day {index + 1}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-6">
              <div className="print-header mb-6 hidden print:block">
                <h1 className="mb-2 text-3xl font-bold">Analytics Report</h1>
                <p className="mb-1 text-lg">
                  {currentBriefing?.festival_name || "Production Analytics"}
                </p>
                {selectedDay && (
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(selectedDay), "EEEE, MMMM d, yyyy")}
                  </p>
                )}
              </div>

              <Tabs defaultValue="crew" className="space-y-6">
                <div className="flex items-center justify-between">
                  <TabsList>
                    <TabsTrigger value="crew" className="gap-2">
                      <Users className="h-4 w-4" />
                      Crew performance
                    </TabsTrigger>
                    <TabsTrigger value="stages" className="gap-2">
                      <MapPin className="h-4 w-4" />
                      Stage coverage
                    </TabsTrigger>
                    <TabsTrigger value="compliance" className="gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Assignments
                    </TabsTrigger>
                    <TabsTrigger value="live" className="gap-2">
                      <Radio className="h-4 w-4" />
                      Live monitoring
                    </TabsTrigger>
                  </TabsList>

                  <div className="flex items-center gap-2 print:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportToPDF}
                      className="gap-2"
                      disabled={!selectedDay}
                    >
                      <Printer className="h-4 w-4" />
                      Export PDF
                    </Button>
                  </div>
                </div>

                {/* Crew Performance Tab */}
                <TabsContent
                  value="crew"
                  className="space-y-4"
                  data-tab-name="Crew Performance"
                  forceMount
                >
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Crew member statistics</CardTitle>
                          <CardDescription>
                            Performance metrics for each crew member
                            {selectedDay
                              ? ` on ${format(new Date(selectedDay), "MMMM d, yyyy")}`
                              : ""}
                          </CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportCrewPerformanceCSV}
                          className="gap-2 print:hidden"
                          disabled={!selectedDay || !presenceReady}
                        >
                          <FileText className="h-4 w-4" />
                          Export CSV
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Crew member</TableHead>
                            <TableHead>Stages visited</TableHead>
                            <TableHead>Downtime</TableHead>
                            <TableHead>Visits</TableHead>
                            <TableHead>Avg per Visit</TableHead>
                            <TableHead>Most Time At</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {crewPerformance.length > 0 ? (
                            crewPerformance.map((perf) => (
                              <TableRow key={perf.crew.id}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-3 w-3 rounded-full"
                                      style={{ backgroundColor: perf.crew.color || "#3b82f6" }}
                                    />
                                    <div>
                                      <p className="font-medium">{perf.crew.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {perf.crew.function}
                                      </p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>{perf.totalStages}</TableCell>
                                <TableCell>
                                  {perf.downtime > 0 ? (
                                    <div className="flex items-center gap-1">
                                      <WifiOff className="h-3 w-3 text-yellow-500" />
                                      <span className="font-medium text-yellow-500">
                                        {formatDuration(perf.downtime)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>{perf.visits}</TableCell>
                                <TableCell>{formatDuration(perf.avgMinutes)}</TableCell>
                                <TableCell>
                                  {perf.mostVisitedStage ? (
                                    <div>
                                      <p className="text-sm font-medium">
                                        {perf.mostVisitedStage.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {formatDuration(perf.mostVisitedStage.minutes)}
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-muted-foreground">
                                {emptyStateLabel}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Stage Coverage Tab */}
                <TabsContent
                  value="stages"
                  className="space-y-4"
                  data-tab-name="Stage Coverage"
                  forceMount
                >
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>Stage coverage summary</CardTitle>
                            <CardDescription>Total filming time per stage</CardDescription>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={exportStageCoverageCSV}
                            className="gap-2 print:hidden"
                            disabled={!selectedDay || !presenceReady}
                          >
                            <FileText className="h-4 w-4" />
                            Export CSV
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Stage</TableHead>
                              <TableHead>Detected</TableHead>
                              <TableHead>Expected</TableHead>
                              <TableHead>Coverage</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Assignments</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {stageCoverage.length > 0 ? (
                              stageCoverage.map((stage) => (
                                <TableRow key={stage.stageId ?? stage.stage}>
                                  <TableCell className="font-medium">{stage.stage}</TableCell>
                                  <TableCell>{formatDuration(stage.totalMinutes)}</TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {stage.expectedMinutes > 0
                                      ? formatDuration(stage.expectedMinutes)
                                      : "—"}
                                  </TableCell>
                                  <TableCell>
                                    {stage.coveragePct != null ? `${stage.coveragePct}%` : "—"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant={
                                        stage.status === "covered"
                                          ? "default"
                                          : stage.status === "partial"
                                            ? "secondary"
                                            : stage.status === "low" || stage.status === "no_gps"
                                              ? "destructive"
                                              : "outline"
                                      }
                                    >
                                      {stage.status === "covered"
                                        ? "✓ Covered"
                                        : stage.status === "partial"
                                          ? "⚠ Partial"
                                          : stage.status === "low"
                                            ? "⚠ Low"
                                            : stage.status === "no_gps"
                                              ? "✕ No GPS"
                                              : "—"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {stage.assignmentCount > 0
                                      ? `${stage.detectedAssignmentCount} / ${stage.assignmentCount}` +
                                        ` (${Math.round((stage.detectedAssignmentCount / stage.assignmentCount) * 100)}%)`
                                      : "—"}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  className="text-center text-muted-foreground"
                                >
                                  {emptyStateLabel}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    {/* Coverage bar chart: presence / expected per stage, capped at 100% for bar
                        width but raw % shown next to it (can exceed 100% when crew hung out at the
                        stage longer than their assignments required). */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Coverage per stage</CardTitle>
                        <CardDescription>
                          Detected presence vs scheduled assignment time. Above 100% = crew spent
                          longer at the stage than scheduled.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {stageCoverage.length > 0 ? (
                          <div className="space-y-4">
                            {stageCoverage
                              .filter((s) => s.assignmentCount > 0)
                              .sort((a, b) => (b.coveragePct ?? 0) - (a.coveragePct ?? 0))
                              .map((stage) => {
                                const pct = stage.coveragePct ?? 0;
                                const barWidth = Math.min(100, pct);
                                const color = getStageColor(stage.stage);
                                return (
                                  <div key={stage.stage} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="font-medium">{stage.stage}</span>
                                      <span className="text-muted-foreground">
                                        {formatDuration(stage.totalMinutes)} /{" "}
                                        {stage.expectedMinutes > 0
                                          ? formatDuration(stage.expectedMinutes)
                                          : "—"}{" "}
                                        ({pct}%)
                                      </span>
                                    </div>
                                    <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
                                      <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                          width: `${barWidth}%`,
                                          backgroundColor: color,
                                          minWidth: pct > 0 ? "8px" : "0px",
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                            {emptyStateLabel}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Assignment Compliance Tab */}
                <TabsContent
                  value="compliance"
                  className="space-y-4"
                  data-tab-name="Assignment Compliance"
                  forceMount
                >
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Assignments</CardTitle>
                          <CardDescription>
                            Check if crew members were present during their assigned times
                          </CardDescription>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 print:hidden">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search..."
                              value={assignmentSearch}
                              onChange={(e) => setAssignmentSearch(e.target.value)}
                              className="w-[160px] pl-8"
                            />
                          </div>
                          <Select
                            value={complianceStageFilter}
                            onValueChange={setComplianceStageFilter}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="All stages" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All stages</SelectItem>
                              {uniqueStagesForFilter.map((stage: string) => (
                                <SelectItem key={stage} value={stage}>
                                  {stage}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={complianceCrewFilter}
                            onValueChange={setComplianceCrewFilter}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="All crew" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All crew</SelectItem>
                              {uniqueCrewForFilter.map((crew: Tables<"crew_members">) => (
                                <SelectItem key={crew.id} value={crew.id}>
                                  {crew.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={complianceSortBy}
                            onValueChange={(v: "time" | "crew" | "stage") => setComplianceSortBy(v)}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue placeholder="Sort by..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="time">Sort by time</SelectItem>
                              <SelectItem value="crew">Sort by crew</SelectItem>
                              <SelectItem value="stage">Sort by stage</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={exportComplianceCSV}
                            className="gap-2"
                            disabled={!selectedDay}
                          >
                            <FileText className="h-4 w-4" />
                            Export CSV
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">GPS confidence</span>
                        <MatchConfidenceBadge source="strict" distanceM={0} />
                        <span>strictly inside the stage polygon</span>
                        <MatchConfidenceBadge source="within_accuracy" distanceM={12} />
                        <span>within ping accuracy of the edge (drift)</span>
                        <MatchConfidenceBadge source="no_presence" />
                        <span>crew not seen at the assigned stage</span>
                        <MatchConfidenceBadge source={null} />
                        <span>metadata predates the classifier</span>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Assignment</TableHead>
                            <TableHead>Crew member</TableHead>
                            <TableHead>Stage</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead>Present?</TableHead>
                            <TableHead>Attendance</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAssignmentCompliance.length > 0 ? (
                            filteredAssignmentCompliance.map((comp: ComplianceItem) => (
                              <TableRow key={comp.assignment.id}>
                                <TableCell className="font-medium">
                                  {comp.assignment.artist_name || comp.assignment.subject}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-3 w-3 rounded-full"
                                      style={{ backgroundColor: comp.crew.color || "#3b82f6" }}
                                    />
                                    {comp.crew.name}
                                  </div>
                                </TableCell>
                                <TableCell>{comp.assignment.stage_name}</TableCell>
                                <TableCell className="font-mono text-sm">
                                  {formatTime(comp.assignment.filming_start)} -{" "}
                                  {formatTime(comp.assignment.filming_end)}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const kind = resolveComplianceIconKind({
                                      present: comp.present,
                                      markedAsDone: comp.markedAsDone,
                                      hasOverride: !!comp.override,
                                      matchSource: comp.matchConfidence?.source ?? null,
                                    });
                                    // Producer override: keep the underlying
                                    // present/absent look + a pencil marker.
                                    if (kind === "override") {
                                      const overrideIcon = comp.present ? (
                                        <CheckCircle
                                          className={
                                            comp.matchConfidence?.source === "within_accuracy"
                                              ? "h-5 w-5 text-yellow-500"
                                              : "h-5 w-5 text-green-500"
                                          }
                                        />
                                      ) : (
                                        <XCircle className="h-5 w-5 text-red-500" />
                                      );
                                      return (
                                        <div className="flex items-center gap-1">
                                          {overrideIcon}
                                          <Pencil className="h-3 w-3 text-muted-foreground" />
                                        </div>
                                      );
                                    }
                                    if (kind === "unknown") {
                                      return (
                                        <HelpCircle
                                          className="h-5 w-5 text-muted-foreground"
                                          aria-label="Unknown — metadata predates the classifier"
                                        />
                                      );
                                    }
                                    if (kind === "absent") {
                                      return <XCircle className="h-5 w-5 text-red-500" />;
                                    }
                                    // present | drift → green / yellow check
                                    return (
                                      <CheckCircle
                                        className={
                                          kind === "drift"
                                            ? "h-5 w-5 text-yellow-500"
                                            : "h-5 w-5 text-green-500"
                                        }
                                      />
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Progress value={comp.attendance} className="h-2 w-24" />
                                      <span className="text-sm font-medium">
                                        {comp.attendance}%
                                      </span>
                                      {comp.matchConfidence ? (
                                        <MatchConfidenceBadge
                                          source={comp.matchConfidence.source}
                                          distanceM={comp.matchConfidence.worstDistanceM}
                                        />
                                      ) : null}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {formatDuration(Math.round(comp.presentMinutes))} /{" "}
                                      {formatDuration(Math.round(comp.totalMinutes))}
                                    </p>
                                    {comp.override && (
                                      <p className="flex items-center gap-1 text-xs text-blue-400">
                                        <Pencil className="h-3 w-3" />
                                        Manually adjusted
                                        {comp.override.note && <span>• {comp.override.note}</span>}
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleOpenOverrideDialog(comp)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground">
                                {presenceReady ? "No assignments to check" : emptyStateLabel}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Live Crew Monitoring Tab */}
                <TabsContent value="live" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Live crew status</CardTitle>
                      <CardDescription>
                        Real-time monitoring of crew member settings and GPS status
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Crew member</TableHead>
                            <TableHead>Function</TableHead>
                            <TableHead>Push notifications</TableHead>
                            <TableHead>GPS enabled</TableHead>
                            <TableHead>GPS status</TableHead>
                            <TableHead>Last GPS update</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {crewStatus && crewStatus.length > 0 ? (
                            crewStatus.map((crew: CrewMemberWithStatus) => (
                              <TableRow key={crew.id}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-3 w-3 rounded-full"
                                      style={{ backgroundColor: crew.color || "#3b82f6" }}
                                    />
                                    {crew.name}
                                  </div>
                                </TableCell>
                                <TableCell>{crew.function}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {crew.pushEnabled ? (
                                      <>
                                        <Bell className="h-4 w-4 text-green-500" />
                                        <span className="text-sm">Enabled</span>
                                      </>
                                    ) : (
                                      <>
                                        <BellOff className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">
                                          Disabled
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {crew.hasGPS ? (
                                      <>
                                        <MapPin className="h-4 w-4 text-green-500" />
                                        <span className="text-sm">Enabled</span>
                                      </>
                                    ) : (
                                      <>
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">
                                          Disabled
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {crew.gpsLive ? (
                                    <Badge variant="default" className="bg-green-500">
                                      <Radio className="mr-1 h-3 w-3" />
                                      Live
                                    </Badge>
                                  ) : crew.lastLocation ? (
                                    <Badge variant="outline">Offline</Badge>
                                  ) : (
                                    <Badge variant="secondary">No Data</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {crew.lastLocation ? (
                                    <>
                                      {format(new Date(crew.lastLocation), "HH:mm:ss")}
                                      <span className="block text-xs">
                                        ({formatTimeAgo(new Date(crew.lastLocation).getTime())})
                                      </span>
                                    </>
                                  ) : (
                                    "Never"
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-muted-foreground">
                                No crew members found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </ScrollArea>
        </PageTransition>
      )}

      {/* Override Dialog */}
      <Dialog
        open={!!editingAssignment}
        onOpenChange={(open) => {
          if (!open) setEditingAssignment(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust attendance</DialogTitle>
            <DialogDescription>
              Manually correct the attendance data for this assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="override-present">Was present?</Label>
              <Switch
                id="override-present"
                checked={overridePresent}
                onCheckedChange={setOverridePresent}
              />
            </div>
            {overridePresent && (
              <div className="space-y-2">
                <Label htmlFor="override-minutes">Minutes present</Label>
                <p className="text-sm text-muted-foreground">
                  Expected duration: {editingAssignmentDuration} minutes
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="override-minutes"
                    type="number"
                    min="0"
                    placeholder="e.g. 15"
                    value={overrideMinutes}
                    onChange={(e) => setOverrideMinutes(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOverrideMinutes(editingAssignmentDuration.toString())}
                  >
                    Full ({editingAssignmentDuration}m)
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAssignment(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOverride} disabled={saveOverrideMutation.isPending}>
              {saveOverrideMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {briefingId && <EditProductionFAB briefingId={briefingId} />}
    </div>
  );
};

export default Analytics;
